import React, { useEffect, useState, useMemo } from "react";
import { adminAPI } from "../services/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search,
  AlertTriangle,
  Package,
  Loader2,
  Trash2,
  ImagePlus,
  Save,
  Edit3,
} from "lucide-react";
import "../inventory.css";

const UpdateInventory = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    category: "",
    brand: "",
    description: "",
    externalId: "",
    countInStock: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const categories = [
    "men's clothing",
    "women's clothing",
    "electronics",
    "jewelery",
    "footwear",
    "accessories",
  ];

  // ================= FETCH PRODUCTS =================
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await adminAPI.getAllProducts();
        setProducts(data);
      } catch {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // ================= LOAD SELECTED PRODUCT =================
  useEffect(() => {
    if (selectedProduct) {
      setFormData(selectedProduct);
      setPreview(selectedProduct.image);
      setImageFile(null);
    }
  }, [selectedProduct]);

  // ================= HANDLERS =================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // ================= UPDATE PRODUCT =================
  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      if (imageFile) formDataToSend.append("image", imageFile);

      setIsUploading(true);
      setUploadProgress(0);

      const toastId = toast.loading("Updating product... 0%");

      const { data } = await adminAPI.updateProduct(
        selectedProduct._id,
        formDataToSend,
        {
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percent);
            toast.update(toastId, {
              render: `Updating product... ${percent}%`,
              isLoading: true,
            });
          },
        }
      );

      toast.update(toastId, {
        render: "Product updated successfully",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      setProducts((prev) =>
        prev.map((p) =>
          p._id === selectedProduct._id ? data.product : p
        )
      );

      setSelectedProduct(data.product);
    } catch {
      toast.error("Update failed");
    } finally {
      setIsUploading(false);
    }
  };

  // ================= FILTER PRODUCTS =================
  const filteredProducts = useMemo(() => {
    return products.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [products, search]);

  // ================= TABLE ROWS =================
  const productRows = useMemo(() => {
    return filteredProducts.map((product) => {
      const lowStock = product.countInStock <= 5;

      return (
        <tr
          key={product._id}
          onClick={() => setSelectedProduct(product)}
          className={`inventory-row ${selectedProduct?._id === product._id ? "inventory-row-active" : ""} cursor-pointer`}
          style={{backgroundColor: selectedProduct?._id === product._id ? "#e3f2fd" : "", transition: "all 0.2s"}}
        >
          <td className="fw-semibold">{product.name}</td>
          <td className="text-success fw-bold">₹{product.price}</td>
          <td className={`text-center fw-bold ${lowStock ? "text-danger" : "text-success"}`}>
            {product.countInStock}
            {lowStock && <AlertTriangle size={14} className="ms-1 text-danger" />}
          </td>
          <td><span className="badge bg-info text-dark">{product.category}</span></td>
          <td>{product.brand}</td>
        </tr>
      );
    });
  }, [filteredProducts, selectedProduct]);

  // ================= LOADING =================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <Loader2 size={48} className="text-primary mb-3" style={{animation: "spin 1s linear infinite"}} />
          <p className="text-muted fw-semibold">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-container py-4">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick={true} rtl={false} pauseOnFocusLoss={true} draggable={true} />
      <div className="row g-4 align-stretch">

        {/* ================= LEFT PANEL ================= */}
        <div className="col-lg-6 d-flex">
          <div className="inventory-card w-100">
            <div className="card-body d-flex flex-column">

              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <h5 className="fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                  <Package size={20} className="text-success" /> Inventory Products
                </h5>

                <div className="inventory-search input-group input-group-sm">
                  <span className="input-group-text bg-primary text-white border-primary">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control border-primary"
                    placeholder="🔍 Search product..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{maxWidth: "200px"}}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="inventory-table-wrapper flex-grow-1">
                <table className="table table-hover align-middle inventory-table">
                  <thead className="sticky-top table-dark">
                    <tr>
                      <th className="fw-bold">Name</th>
                      <th className="fw-bold">Price</th>
                      <th className="fw-bold text-center">Stock</th>
                      <th className="fw-bold">Category</th>
                      <th className="fw-bold">Brand</th>
                    </tr>
                  </thead>
                  <tbody>{productRows}</tbody>
                </table>

                {filteredProducts.length === 0 && (
                  <div className="text-center mt-5">
                    <Package size={48} className="text-muted mb-2" style={{opacity: 0.5}} />
                    <p className="text-muted fw-semibold">No products found</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ================= RIGHT PANEL ================= */}
        <div className="col-lg-6 d-flex">
        <div className="inventory-card editor-panel w-100">
            <div className="card-body">

            {!selectedProduct ? (
                <div className="empty-editor text-center py-5">
                  <Edit3 size={48} className="text-muted mb-3" style={{opacity: 0.5}} />
                  <p className="text-muted fw-semibold">Select a product to edit inventory</p>
                </div>
            ) : (
                <>
                {/* HEADER */}
                <div className="editor-header mb-3 pb-3 border-bottom d-flex flex-wrap gap-2 align-items-center justify-content-between">

                <h5 className="fw-bold text-primary mb-0 text-truncate d-flex align-items-center gap-2">
                    <Edit3 size={18} />
                    {selectedProduct.name}
                </h5>

                <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-2"
                    disabled={isUploading}
                    onClick={async () => {
                        if (window.confirm("Are you sure you want to delete this product? This action cannot be undone.")) {
                          try {
                            const toastId = toast.loading("Deleting product...");
                            await adminAPI.deleteProduct(selectedProduct._id);
                            toast.update(toastId, {
                              render: "✓ Product deleted successfully",
                              type: "success",
                              isLoading: false,
                              autoClose: 2000,
                            });
                            setProducts((prev) =>
                              prev.filter((p) => p._id !== selectedProduct._id)
                            );
                            setSelectedProduct(null);
                          } catch (error) {
                            toast.error("Failed to delete product. Please try again.");
                          }
                        }
                    }}
                    >
                    <Trash2 size={16} />
                    Delete Product
                    </button>
                </div>
                
                {/* FORM */}
                <form onSubmit={handleUpdate}>

                    <div className="mb-3">
                    <label className="form-label fw-semibold small">📝 Product Name</label>
                    <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-control border-primary"
                        required
                    />
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold small">🏷️ Brand</label>
                    <input
                        name="brand"
                        value={formData.brand}
                        onChange={handleChange}
                        className="form-control border-primary"
                        required
                    />
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold small">💰 Price (₹)</label>
                    <input
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="form-control border-primary"
                        required
                    />
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-bold small text-danger">
                        📦 Stock Quantity
                    </label>
                    <input
                        type="number"
                        name="countInStock"
                        value={formData.countInStock}
                        onChange={handleChange}
                        className="form-control border-danger editor-stock"
                        required
                    />
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold small">🏪 Category</label>
                    <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="form-select border-primary"
                        required
                    >
                        {categories.map((cat, i) => (
                        <option key={i} value={cat}>{cat}</option>
                        ))}
                    </select>
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold small">📋 Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className="form-control border-primary"
                    />
                    </div>

                    <div className="mb-3">
                    <label className="form-label fw-semibold small d-flex align-items-center gap-2">
                      <ImagePlus size={16} />
                      Replace Image
                    </label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="form-control border-primary"
                    />
                    </div>

                    {preview && (
                      <div className="mb-3">
                        <p className="fw-semibold small text-muted">📸 Preview:</p>
                        <div className="preview-box border border-primary rounded" style={{overflow: "hidden", backgroundColor: "#f8f9fa"}}>
                          <img
                            src={preview}
                            alt={formData.name || "Product preview"}
                            style={{maxHeight: "200px", objectFit: "contain", width: "100%"}}
                          />
                        </div>
                      </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn-success inventory-save-btn w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                        disabled={isUploading}
                        style={{fontSize: "16px"}}
                    >
                        {isUploading ? (
                          <>
                            <Loader2 size={18} style={{animation: "spin 1s linear infinite"}} />
                            Updating... {uploadProgress}%
                          </>
                        ) : (
                          <>
                            <Save size={18} />
                            Save Changes
                          </>
                        )}
                    </button>

                </form>
                </>
            )}

            </div>
        </div>
        </div>

      </div>
    </div>
  );
};

export default UpdateInventory;
